// The Groove origin must clear its own session before leaving for SSO logout.
// A partner application's logout cannot remove our cookies or browser storage.
const AUTH_KEYS = [
  'user', 'token', 'keycloak_token', 'keycloak_refresh_token',
  'keycloak_id_token', 'keycloak_user', 'redirect', 'show_promotion', 'groove-affiliate-session'
];
const AUTH_COOKIES = ['db71ab6b0872a8afd28afe1adf7de4b5', 'user', 'token', 'scope', 'admin'];

function storage(browser, name) {
  try { return browser[name]; } catch (_) { return null; }
}

function storedIdToken(browser, clientId, issuer) {
  try {
    const token = storage(browser, 'localStorage').getItem('keycloak_id_token');
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(browser.atob(encoded));
    // This is only a logout hint. Keycloak still validates the token itself.
    return claims.azp === clientId && claims.iss === issuer ? token : null;
  } catch (_) { return null; }
}

export function grooveLogoutUrl(browser, keycloak) {
  const config = browser.gdEnv || {};
  const base = (config.VUE_APP_KEYCLOAK_URL || 'https://auth.groovetech.io/auth').replace(/\/$/, '');
  const realm = config.VUE_APP_KEYCLOAK_REALM || 'gd-apis-live';
  const clientId = config.VUE_APP_KEYCLOAK_CLIENT_ID || 'app-groovecm';
  const issuer = `${base}/realms/${realm}`;
  const url = new URL(`${issuer}/protocol/openid-connect/logout`);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('post_logout_redirect_uri', `${browser.location.origin}/logged-out.html`);
  const idToken = keycloak && keycloak.idToken || storedIdToken(browser, clientId, issuer);
  if (idToken) url.searchParams.set('id_token_hint', idToken);
  return url.href;
}

export function logoutFromGroove({ browser, auth, axios, clearAppSession, keycloak }) {
  // Capture the ID token before cleanup. Without one, Keycloak presents its
  // supported logout confirmation rather than depending on a ScalePlus cookie.
  const destination = grooveLogoutUrl(browser, keycloak);
  try { if (auth) auth.logout(); } catch (_) { /* Continue our own cleanup. */ }
  for (const name of ['localStorage', 'sessionStorage']) {
    const target = storage(browser, name);
    for (const key of AUTH_KEYS) {
      try { if (target) target.removeItem(key); } catch (_) { /* Storage may be blocked. */ }
    }
  }
  clearGrooveAuthCookies(browser);
  const headers = axios && axios.defaults && axios.defaults.headers && axios.defaults.headers.common;
  if (headers) {
    delete headers.Authorization;
    delete headers['x-auth-provider'];
  }
  try { if (clearAppSession) clearAppSession(); } catch (_) { /* Navigation still ends the app session. */ }
  try { if (keycloak && keycloak.clearToken) keycloak.clearToken(); } catch (_) { /* Continue logout. */ }
  browser.location.replace(destination);
}

// Host-only and parent-domain copies both have to expire. gd-lib reads the
// first cookie it finds, so a leftover parent-domain session would win.
export function clearGrooveAuthCookies(browser, cookieNames = AUTH_COOKIES) {
  const hostname = browser.location.hostname;
  const domains = ['', hostname];
  for (const parent of ['groove.cm', 'grooveapps.com', 'groovefunnels.com']) {
    if (hostname === parent || hostname.endsWith(`.${parent}`)) domains.push(parent);
  }
  for (const cookie of cookieNames) {
    for (const path of ['/', '/groovesell', '/groovesell/']) {
      for (const domain of [...new Set(domains)]) {
        try {
          browser.document.cookie = `${cookie}=; Max-Age=0; path=${path}; SameSite=None; Secure${domain ? `; domain=${domain}` : ''}`;
        } catch (_) { /* A restricted cookie jar must not prevent SSO logout. */ }
      }
    }
  }
}
